import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2 } from "lucide-react";

import { listGames, createGame, updateGame, deleteGame } from "./gameApi";
import GameForm from "./GameForm";

import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormButton from "../../../components/form/FormButton";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";

import "./game.css";

const scopeLabel = (game) => {
  if (game.isGlobal) return "Global";
  return game.country ? `Country – ${game.country}` : "Country-specific";
};

const GamesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [busyId, setBusyId] = useState(null);

  const tc = useTableController(items, {
    searchKeys: ["name"],
    initialPageSize: 10,
  });

  const load = async () => {
    setLoading(true);

    try {
      const data = await listGames();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (payload) => {
    try {
      await createGame(payload);
      toast.success("Game created");
      setCreating(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Create failed");
      throw error;
    }
  };

  const handleUpdate = async (payload) => {
    try {
      const id = editing?._id || editing?.id;
      await updateGame(id, payload);
      toast.success("Game updated");
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
      throw error;
    }
  };

  const handleDelete = async () => {
    const id = deleting?._id || deleting?.id;

    if (!id) {
      toast.error("Missing game id");
      return;
    }

    try {
      setBusyId(id);
      await deleteGame(id);
      toast.success("Game deleted");
      setDeleting(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="sa-head-row games-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Games</h1>

        <FormButton
          variant="primary"
          className="games-new-btn"
          onClick={() => setCreating(true)}
        >
          <Plus className="lucide" /> New Game
        </FormButton>
      </div>

      <TableToolbar
        searchTerm={tc.searchTerm}
        onSearchChange={tc.setSearch}
        searchPlaceholder="Search games..."
      />

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.total === 0 ? (
          <div className="sa-empty">
            <div>No games found.</div>

            <FormButton variant="primary" onClick={() => setCreating(true)}>
              Create your first game
            </FormButton>
          </div>
        ) : (
          <div className="sa-table-wrap games-table-wrap">
            <table className="sa-table games-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>S.N.</th>
                  <th style={{ width: 80 }}>Icon</th>

                  <th onClick={() => tc.toggleSort("name")} style={{ cursor: "pointer" }}>
                    Name{" "}
                    {tc.sortKey === "name" ? (tc.sortDir === "asc" ? "▲" : "▼") : ""}
                  </th>

                  <th style={{ width: 160 }}>Scope</th>

                  <th onClick={() => tc.toggleSort("createdAt")} style={{ cursor: "pointer" }}>
                    Created{" "}
                    {tc.sortKey === "createdAt"
                      ? tc.sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>

                  <th style={{ textAlign: "right", width: 100 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {tc.rows.map((game, index) => {
                  const id = game._id || game.id;

                  return (
                    <tr key={id || game.name}>
                      <td>{(tc.page - 1) * tc.pageSize + index + 1}</td>

                      <td>
                        <div className="games-table-icon">
                          {game.iconUrl ? <img src={game.iconUrl} alt="" /> : <span>—</span>}
                        </div>
                      </td>

                      <td>{game.name || "—"}</td>
                      <td>{scopeLabel(game)}</td>

                      <td>
                        {game.createdAt ? new Date(game.createdAt).toLocaleDateString() : "—"}
                      </td>

                      <td className="sa-actions-cell">
                        <div className="sa-actions">
                          <button
                            type="button"
                            className="sa-icon-link"
                            onClick={() => setEditing(game)}
                            title="Edit game"
                            aria-label="Edit game"
                          >
                            <Pencil className="lucide" />
                          </button>

                          <button
                            type="button"
                            className="sa-icon-link danger"
                            onClick={() => setDeleting(game)}
                            disabled={busyId === id}
                            title="Delete game"
                            aria-label="Delete game"
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {creating && (
        <GameForm
          open={creating}
          mode="create"
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <GameForm
          key={editing?._id || editing?.id}
          open={!!editing}
          mode="edit"
          initialData={{
            name: editing.name || "",
            iconUrl: editing.iconUrl || "",
            iconKey: editing.iconKey || "",
            isGlobal:
              typeof editing.isGlobal === "boolean" ? editing.isGlobal : true,
            country: editing.country || "",
          }}
          onCancel={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete Game"
        message={`Are you sure you want to delete "${
          deleting?.name || "this game"
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deleting?._id || deleting?.id)}
        onConfirm={handleDelete}
        onCancel={() => {
          if (busyId !== (deleting?._id || deleting?.id)) {
            setDeleting(null);
          }
        }}
      />
    </>
  );
};

export default GamesPage;